import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useGeminiApi } from '@/hooks/useGeminiApi';
import { RefreshCw, Lightbulb, AlertTriangle } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { LoadingSpinner } from '@/components/ui/loading-spinner';

interface CompetitorInsightsProps {
  competitor: string;
  competitorData: any;
  isLoading?: boolean;
}

export const CompetitorInsights = ({ competitor, competitorData, isLoading = false }: CompetitorInsightsProps) => {
  const [insights, setInsights] = useState<string | null>(null);
  const [isGeneratingInsights, setIsGeneratingInsights] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { generateContent } = useGeminiApi();
  const { t } = useTranslation('competitorAnalysis');
  const currentLanguage = i18n.language; // Get current language

  useEffect(() => {
    // Reset insights when competitor changes
    if (competitor) {
      setInsights(null);
    }
  }, [competitor]);

  useEffect(() => {
    // Generate insights automatically when we have competitor data
    if (competitorData && !insights && !isGeneratingInsights && competitor) {
      generateInsights();
    }
  }, [competitorData, competitor]);

  const generateInsights = async () => {
    if (!competitor || !competitorData || isLoading) return;
    
    setIsGeneratingInsights(true);
    setError(null);
    
    try {
      const dataForAI = {
        competitor,
        totalContracts: competitorData.stats?.totalContracts || 0,
        totalValue: competitorData.stats?.totalValue || 0,
        averageValue: competitorData.stats?.averageValue || 0,
        recentActivity: competitorData.stats?.recentContractsCount || 0,
        topContracts: competitorData.topContracts || [],
        contractsByCountry: competitorData.contractsByCountry || [],
        valueByCountry: competitorData.valueByCountry || [],
        contractsByPeriod: competitorData.contractsByPeriod || [],
        valueDistribution: competitorData.valueDistribution || [],
      };
      
      // Create language-specific system instructions
      const systemInstruction = currentLanguage === 'da'
        ? `Du er en specialiseret udbudsekspert, der hjælper virksomheder med at forstå deres konkurrenter på det europæiske offentlige udbudsmarked. 
          Analysér de leverede konkurrentdata og giv en strategisk analyse med indsigt om: 
          1. Markedspositionering og styrker 
          2. Geografisk fokus og ekspansionsmønstre 
          3. Kontraktværditendenser og præferencer 
          4. Strategiske anbefalinger til at konkurrere mod denne virksomhed 
          5. Potentielle muligheder baseret på huller i deres portefølje 
          Formatér din respons med kun sektionsoverskrifter og punktopstillinger hvor det er passende. 
          Hold din analyse kortfattet, datadrevet og handlingsorienteret. 
          Start din analyse med en klar overskrift. Inkluder ikke nogen indledende sætninger, der nævner, at du analyserer dataene eller andre meta-kommentarer. 
          Inkluder ikke afsluttende bemærkninger som "held og lykke" eller lignende udtryk. Fokusér kun på selve analysen.
          Sørg for god formatering med mellemrum mellem de forskellige afsnit og punkter.
          Du SKAL svare udelukkende på dansk sprog.`
        : `You are a specialized procurement expert helping businesses understand their competitors in the European public procurement market. 
          Analyze the provided competitor data and provide a strategic analysis with insights on: 
          1. Market positioning and strengths 
          2. Geographic focus and expansion patterns 
          3. Contract value trends and preferences 
          4. Strategic recommendations for competing against this company 
          5. Potential opportunities based on gaps in their portfolio 
          Format your response with only section headers and bullet points where appropriate. 
          Keep your analysis concise, data-driven and actionable. 
          Start your analysis with a clear heading. Do not include any introductory phrases mentioning that you are analyzing the data or other meta-commentary. 
          Do not include concluding remarks like "good luck" or similar phrases. Focus only on the analysis itself.
          Ensure good formatting with spacing between different sections and bullet points.`;
      
      // Create language-specific prompt
      const prompt = currentLanguage === 'da'
        ? `Analysér venligst disse konkurrentdata og giv strategiske indsigter: ${JSON.stringify(dataForAI)}`
        : `Please analyze this competitor data and provide strategic insights: ${JSON.stringify(dataForAI)}`;
      
      const response = await generateContent(prompt, {
        model: "gemini-2.0-flash",
        temperature: 0.2, 
        systemInstruction,
      });
      
      if (response && response.generatedText) {
        setInsights(response.generatedText);
      } else {
        setError(currentLanguage === 'da' 
          ? "Kunne ikke generere indsigter. Prøv venligst igen." 
          : "Failed to generate insights. Please try again.");
      }
    } catch (err) {
      console.error("Error generating competitor insights:", err);
      setError(currentLanguage === 'da'
        ? "Der opstod en fejl under generering af indsigter. Prøv venligst igen."
        : "An error occurred while generating the insights. Please try again.");
    } finally {
      setIsGeneratingInsights(false);
    }
  };

  // Render loading state
  if (isGeneratingInsights) {
    return (
      <Card className="mt-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl flex items-center">
              <Lightbulb className="h-5 w-5 mr-2 text-amber-500" />
              {currentLanguage === 'da' ? 'Genererer indsigter...' : 'Generating insights...'}
            </CardTitle>
            <Badge variant="outline" className="animate-pulse bg-amber-50">
              {currentLanguage === 'da' ? 'Behandler...' : 'Processing...'}
            </Badge>
          </div>
          <CardDescription>
            {currentLanguage === 'da' ? 'Analyserer...' : 'Analyzing...'}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center py-8">
          <LoadingSpinner size={32} className="mb-4 text-primary"/>
          <p className="text-sm text-muted-foreground">
            {currentLanguage === 'da' ? 'Dette kan tage et øjeblik...' : 'This may take a moment...'}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (error) {
    return (
      <Card className="mt-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl flex items-center">
              <Lightbulb className="h-5 w-5 mr-2 text-amber-500" />
              {currentLanguage === 'da' ? 'Konkurrentanalyse' : 'Competitor Analysis'}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={generateInsights}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {currentLanguage === 'da' ? 'Prøv igen' : 'Retry'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>
              {currentLanguage === 'da' ? 'Fejl' : 'Error'}
            </AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Render insights or empty state
  return (
    <Card className="mt-6">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-xl flex items-center">
            <Lightbulb className="h-5 w-5 mr-2 text-amber-500" />
            {currentLanguage === 'da' ? 'Konkurrentanalyse' : 'Competitor Analysis'}
          </CardTitle>
          {insights && (
            <Button
              variant="outline"
              size="sm"
              onClick={generateInsights}
              className="flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              {currentLanguage === 'da' ? 'Opdater' : 'Refresh'}
            </Button>
          )}
        </div>
        <CardDescription>
          {insights 
            ? (currentLanguage === 'da' 
                ? `Strategisk analyse af ${competitor}` 
                : `Strategic analysis of ${competitor}`)
            : (currentLanguage === 'da' 
                ? 'Søg efter en konkurrent for at generere en analyse' 
                : 'Search for a competitor to generate analysis')
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!insights && !competitor ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Lightbulb className="h-12 w-12 text-amber-400 mb-3" />
            <p className="text-muted-foreground">
              {currentLanguage === 'da' 
                ? 'Søg efter en konkurrent for at generere indsigter'
                : 'Search for a competitor to generate insights'}
            </p>
          </div>
        ) : !insights && competitor ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Button onClick={generateInsights} variant="default">
              <Lightbulb className="h-4 w-4 mr-2" />
              {currentLanguage === 'da' 
                ? `Generér indsigter om ${competitor}` 
                : `Generate insights about ${competitor}`}
            </Button>
          </div>
        ) : (
          <div className="prose max-w-none text-sm">
            <div dangerouslySetInnerHTML={{ __html: formatMarkdown(insights || '') }} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Enhanced formatMarkdown function for better formatting and spacing
const formatMarkdown = (text: string): string => {
  return text
    // Headers with proper spacing
    .replace(/^#\s+(.*$)/gm, '<h2 class="text-xl font-semibold mt-6 mb-3">$1</h2>')
    .replace(/^##\s+(.*$)/gm, '<h3 class="text-lg font-semibold mt-5 mb-2">$1</h3>')
    .replace(/^###\s+(.*$)/gm, '<h4 class="text-base font-semibold mt-4 mb-1">$1</h4>')
    
    // Text formatting
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    
    // Lists with proper spacing
    .replace(/^\*\s+(.*)$/gm, '<li class="ml-4 mb-2">$1</li>')
    
    // Group list items
    .replace(/<\/li>\n<li/g, '</li><li')
    
    // Add proper list markup
    .replace(/<li class="ml-4 mb-2">/g, '<ul class="list-disc space-y-2 my-3 pl-2"><li class="ml-4 mb-2">')
    .replace(/<\/li>(?!\s*<li)/g, '</li></ul>')
    
    // Fix any double-nested list issues
    .replace(/<ul class="list-disc space-y-2 my-3 pl-2"><li class="ml-4 mb-2"><ul class="list-disc space-y-2 my-3 pl-2">/g, 
            '<ul class="list-disc space-y-2 my-3 pl-2"><li class="ml-4 mb-2">')
    .replace(/<\/ul><\/li><\/ul>/g, '</li></ul>')
    
    // Paragraph spacing
    .replace(/\n\n/g, '<br/><br/>');
};
